import { useMemo } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import { AppBar, Toolbar, useMediaQuery } from '@mui/material';

// project-imports
import AppBarStyled from './AppBarStyled';
import HeaderContent from './HeaderContent';
import IconButton from 'components/@extended/IconButton';

import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';
import useConfig from 'hooks/useConfig';
import { dispatch, useSelector } from 'store';
import { openDrawer } from 'store/reducers/menu';
import { MenuOrientation } from 'config';
import { getSidebarBg } from '../Drawer/MiniDrawerStyled';

// assets
import { HambergerMenu } from 'iconsax-react';

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

const Header = () => {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { menuOrientation } = useConfig();
  const { drawerOpen } = useSelector((state) => state.menu);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // header content
  const headerContent = useMemo(() => <HeaderContent />, []);

  // common header
  const mainHeader = (
    <>
      {/* px matches the body content's own left/right inset (the "main" Box's px: {xs:2, md:3}
          in mainlayout_vms/index.js) so the header's icon/profile pill line up with the card
          edges below instead of sitting more inset than the body once the body stopped having
          its own extra Container gutters. */}
      <Toolbar sx={{ px: { xs: 2, md: 3 }, minHeight: { xs: 52, sm: 63 }, py: 0 }}>
        {!isHorizontal ? (
          <IconButton
            aria-label="open drawer"
            onClick={() => dispatch(openDrawer(!drawerOpen))}
            // No edge="start" — that applies MUI's own -12px compensation margin, which is tuned
            // for MUI's own default Toolbar padding, not this Toolbar's custom px. With it, the
            // button's own left edge landed to the left of where the body content's heading
            // starts below; without it, the button's outer edge sits flush with the Toolbar's own
            // px (which now matches the body's own left inset — see the comment above).
            variant="light"
            size="large"
            sx={{
              color: '#ffffff',
              bgcolor: alpha(theme.palette.primary.main, drawerOpen ? 0.24 : 0.16),
              p: 1,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.24) }
            }}
          >
            <HambergerMenu />
          </IconButton>
        ) : null}
        {headerContent}
      </Toolbar>
    </>
  );

  // app-bar params - matches the navy sidebar so header + nav read as one chrome surface
  const appBar = {
    position: 'fixed',
    elevation: 0,
    sx: {
      bgcolor: alpha(getSidebarBg(), 0.88),
      backdropFilter: 'blur(8px)',
      zIndex: 1200,
      width: isHorizontal
        ? '100%'
        : { xs: '100%', lg: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${MINI_DRAWER_WIDTH}px)` }
    }
  };

  return (
    <>
      {!downLG ? (
        <AppBarStyled open={drawerOpen} {...appBar}>
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  );
};

export default Header;
